import { useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowUturnDownIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../../components/base/Card';
import { DataTable } from '../../../components/base/Table';
import { Button } from '../../../components/base/Button';
import { ConfirmDelete } from '../../../components/shared/ConfirmDelete';
import { IFaultCause } from '../../../types/Fault/FaultCause';
import useSuperAdminFaultCauseMutations from '../../../data/fault/superadmin/useSuperAdminFaultCauseMutations';
import { useClipboardCopy } from '../../../hooks/useClipboardCopy';
import { faultQueryKeys } from '../../../data/QueryKeys';
import CreateEditFaultCause from '../../../components/Fault/CreateEditFaultCause';

const colHelper = createColumnHelper<IFaultCause>();

export function FaultCausesPage() {
  const { CopyToClipboardButton } = useClipboardCopy();
  const { delFaultCause, updateFaultCause } =
    useSuperAdminFaultCauseMutations();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteFaultCauseItem, setDeleteFaultCauseItem] = useState<
    IFaultCause | undefined
  >(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFaultCause, setSelectedFaultCause] = useState<
    IFaultCause | undefined
  >(undefined);

  const handleDeleteFaultCause = (faultCause: IFaultCause) => {
    setDeleteFaultCauseItem(faultCause);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteFaultCause = () => {
    delFaultCause.mutate(deleteFaultCauseItem!);
    setDeleteOpen(false);
  };

  const handleRestoreFaultCause = (faultCause: IFaultCause) => {
    updateFaultCause.mutate({ ...faultCause, restore: true });
  };

  const handleEditFaultCause = (faultCause: IFaultCause) => {
    setSelectedFaultCause(faultCause);
    setModalOpen(true);
  };

  const handleAddFaultCause = () => {
    setSelectedFaultCause(undefined);
    setModalOpen(true);
  };

  const handleModalClose = (refresh?: boolean) => {
    setModalOpen(false);
    setSelectedFaultCause(undefined);
    if (refresh) {
      // The table will automatically refresh due to query invalidation in the mutation
    }
  };

  const cols = [
    colHelper.accessor('title', { header: 'Title' }),
    colHelper.accessor('description', {
      header: 'Description',
      cell: (info) => (
        <div className="max-w-xs truncate">{info.getValue()}</div>
      ),
    }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          {info.cell.row.original.deletedOn ? (
            <ArrowUturnDownIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() => handleRestoreFaultCause(info.row.original)}
            />
          ) : (
            <>
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
              <PencilSquareIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleEditFaultCause(info.row.original)}
              />
              <TrashIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleDeleteFaultCause(info.row.original)}
              />
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <ConfirmDelete
        itemName={'Fault Cause'}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteFaultCause()}
      />
      <CreateEditFaultCause
        open={modalOpen}
        onClose={handleModalClose}
        faultCause={selectedFaultCause}
      />
      <Card title="Fault Causes">
        <div className="my-6 space-x-4">
          <Button onClick={handleAddFaultCause}>Add Fault Cause</Button>
        </div>
        <div>
          <DataTable<IFaultCause>
            url="/superadmin/fault/causes"
            queryKey={faultQueryKeys.superAdminCauses()}
            columns={cols}
            enableShowDeleted
          />
        </div>
      </Card>
    </>
  );
}
